import React, { useEffect, useState, useCallback, useRef, createContext, useContext } from 'react';
import {
  Play,
  Compass,
  PenTool,
  Sparkles,
  Video,
  Mic,
  Upload,
  ArrowRight,
  Mail,
  Linkedin,
  Quote,
  BookOpen,
  Heart,
  Lightbulb,
  Layers,
  MessageCircle,
  Globe,
  GraduationCap,
  Workflow,
  UserRound,
  Wand2,
  RefreshCw,
  FileText,
  Search,
  MessageSquare,
  NotebookPen,
  Image as ImageIcon,
  Download,
  Lock,
  ArrowUpRight,
  TrendingUp,
  Repeat,
  Megaphone,
  Camera,
  Brain,
  X,
} from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from './ui/accordion';
import AnalyticsTracker from './AnalyticsTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Image Lightbox ==============
const LightboxContext = createContext({ open: () => {} });
const useLightbox = () => useContext(LightboxContext);

const LightboxProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, src: '', alt: '' });
  const open = useCallback((src, alt = '') => {
    if (src) setState({ open: true, src, alt });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [state.open, close]);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      {state.open ? (
        <div
          onClick={close}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-6 md:p-10 cursor-zoom-out"
          data-testid="lightbox-overlay"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-md"
            data-testid="lightbox-close"
            aria-label="Close image preview"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={state.src}
            alt={state.alt}
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      ) : null}
    </LightboxContext.Provider>
  );
};

// ============== Zoomable image wrapper ==============
const ZoomImage = ({ src, alt = '', className = '', ...rest }) => {
  const { open } = useLightbox();
  return (
    <img
      src={src}
      alt={alt}
      className={`cursor-zoom-in ${className}`}
      onClick={() => open(src, alt)}
      {...rest}
    />
  );
};

// ============== Cursor trail ==============
const CursorTrail = () => {
  const [binaries, setBinaries] = useState([]);

  const handleMouseMove = useCallback((e) => {
    const id = Date.now() + Math.random();
    const ch = Math.random() > 0.5 ? '1' : '0';
    setBinaries((prev) => [
      ...prev.slice(-25),
      {
        id,
        x: e.clientX + (Math.random() - 0.5) * 30,
        y: e.clientY + (Math.random() - 0.5) * 30,
        binary: ch,
        opacity: 0.35 + Math.random() * 0.3,
      },
    ]);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    const cleanup = setInterval(() => {
      setBinaries((prev) => prev.slice(1));
    }, 80);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(cleanup);
    };
  }, [handleMouseMove]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {binaries.map((item, index) => {
        const fadeOpacity = ((index + 1) / binaries.length) * item.opacity;
        return (
          <span
            key={item.id}
            className="absolute font-mono text-stone-500 select-none transition-opacity duration-200"
            style={{
              left: item.x,
              top: item.y,
              opacity: fadeOpacity,
              fontSize: `${12 + Math.random() * 6}px`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {item.binary}
          </span>
        );
      })}
    </div>
  );
};

// ============== Admin auth hook ==============
const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.is_admin) setIsAdmin(true);
      } catch (e) {
        // silent: default to public
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return isAdmin;
};

// ============== UI Atoms ==============
const Eyebrow = ({ children, color = 'bg-stone-100 text-stone-600 border-stone-200' }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-medium border ${color}`}
  >
    {children}
  </span>
);

const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`px-6 md:px-10 lg:px-16 py-20 md:py-28 ${className}`}>
    <div className="max-w-5xl mx-auto">{children}</div>
  </section>
);

const FieldNoteHeader = ({ number, title }) => (
  <div className="mb-10 md:mb-14">
    <div className="flex items-center gap-3 mb-6">
      <span className="text-[11px] tracking-[0.22em] uppercase text-rose-500 font-medium">
        Field Note {number}
      </span>
      <span className="h-px flex-1 bg-stone-200" />
    </div>
    <h2
      className="text-3xl md:text-5xl font-serif text-stone-900 leading-[1.1] max-w-4xl"
      style={{ textWrap: 'balance' }}
    >
      {title}
    </h2>
  </div>
);

const Para = ({ children }) => (
  <p
    className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
    style={{ textWrap: 'pretty' }}
  >
    {children}
  </p>
);

const CalloutCard = ({ children }) => (
  <div className="mt-8 rounded-3xl bg-gradient-to-br from-amber-50 via-rose-50 to-stone-50 border border-stone-200 p-7 md:p-9 shadow-sm">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-2xl bg-white border border-stone-200 flex items-center justify-center shrink-0">
        <Quote className="w-5 h-5 text-rose-400" />
      </div>
      <p
        className="text-stone-700 text-[17px] md:text-[18px] leading-relaxed"
        style={{ textWrap: 'pretty' }}
      >
        {children}
      </p>
    </div>
  </div>
);

const SmallCard = ({ icon: Icon, title, copy, accent = 'stone' }) => {
  const accents = {
    stone: 'bg-stone-50 border-stone-200 text-stone-500',
    rose: 'bg-rose-50 border-rose-100 text-rose-500',
    amber: 'bg-amber-50 border-amber-100 text-amber-600',
    sky: 'bg-sky-50 border-sky-100 text-sky-500',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-500',
    violet: 'bg-violet-50 border-violet-100 text-violet-500',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-500',
    teal: 'bg-teal-50 border-teal-100 text-teal-500',
  };
  return (
    <div className="rounded-3xl bg-white border border-stone-200 p-6 md:p-7 hover:border-stone-300 hover:shadow-md transition-all duration-300">
      <div
        className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 border ${accents[accent]}`}
      >
        {Icon ? <Icon className="w-5 h-5" /> : null}
      </div>
      <h4 className="text-stone-900 font-medium text-base md:text-lg mb-2">{title}</h4>
      <p
        className="text-stone-500 text-[14px] md:text-[15px] leading-relaxed"
        style={{ textWrap: 'pretty' }}
      >
        {copy}
      </p>
    </div>
  );
};

// ============== Audio uploader card ==============
const AudioCard = ({ slot, title, accent, clip, onRefresh, isAdmin, quote, presetUrl }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const handleSelect = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      const res = await fetch(`${API}/dear-dia/audio`, { method: 'POST', body: fd });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Upload failed');
      }
      await onRefresh();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await fetch(`${API}/dear-dia/audio/${slot}`, { method: 'DELETE' });
      await onRefresh();
    } finally {
      setUploading(false);
    }
  };

  const togglePlay = (e) => {
    e?.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
    } else {
      a.pause();
    }
  };

  const accentRing = {
    violet: 'bg-violet-100 text-violet-500',
    rose: 'bg-rose-100 text-rose-500',
    amber: 'bg-amber-100 text-amber-500',
    sky: 'bg-sky-100 text-sky-500',
  }[accent];

  return (
    <div
      data-testid={`audio-card-${slot}`}
      className="rounded-2xl bg-white border border-stone-200 p-6 md:p-7 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-4 md:gap-5">
        <button
          type="button"
          onClick={presetUrl || clip ? togglePlay : (isAdmin ? handleSelect : undefined)}
          disabled={!presetUrl && !clip && !isAdmin}
          className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 transition-transform ${accentRing} ${presetUrl || clip || isAdmin ? 'hover:scale-105 cursor-pointer' : 'cursor-default opacity-70'}`}
          aria-label={presetUrl || clip ? (playing ? 'Pause' : 'Play') : 'Upload audio'}
          data-testid={`audio-play-${slot}`}
        >
          <Play className={`w-5 h-5 md:w-6 md:h-6 ${playing ? '' : 'translate-x-[1px]'}`} fill="currentColor" />
        </button>
        <div className="flex-1 min-w-0">
          <h4 className="text-stone-900 font-semibold text-[17px] md:text-[19px] leading-tight">{title}</h4>
          <p className={`text-[13px] md:text-[14px] mt-1 ${(presetUrl || clip) ? 'text-emerald-500' : 'text-stone-400'}`}>
            {presetUrl || clip ? (playing ? 'Playing...' : 'Tap to play') : (isAdmin ? 'Click to upload' : 'Coming soon')}
          </p>
        </div>
        <div className="shrink-0 text-stone-300">
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M15.5 8.5a4 4 0 010 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {presetUrl ? (
        <audio
          ref={audioRef}
          src={presetUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onError={() => setPlaying(false)}
          className="hidden"
          data-testid={`audio-player-${slot}`}
          preload="metadata"
        />
      ) : clip ? (
        <audio
          ref={audioRef}
          src={`${process.env.REACT_APP_BACKEND_URL}${clip.url}`}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden"
          data-testid={`audio-player-${slot}`}
        />
      ) : null}

      {quote ? (
        <div className="mt-5 pt-5 border-t border-stone-100">
          <div className="flex items-start gap-3">
            <Quote className="w-4 h-4 text-violet-300 shrink-0 mt-1" />
            <p
              className="text-stone-600 italic text-[15px] md:text-[16px] leading-[1.6]"
              style={{ textWrap: 'pretty' }}
              data-testid={`audio-quote-${slot}`}
            >
              {quote}
            </p>
          </div>
        </div>
      ) : null}

      {isAdmin && clip ? (
        <div className="mt-4 flex items-center justify-between text-xs text-stone-400">
          <span className="truncate max-w-[60%]">{clip.filename}</span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSelect}
              className="inline-flex items-center gap-1 hover:text-stone-700 transition-colors"
              data-testid={`audio-replace-${slot}`}
            >
              <RefreshCw className="w-3 h-3" /> Replace
            </button>
            <button
              onClick={handleRemove}
              className="hover:text-rose-500 transition-colors"
              data-testid={`audio-remove-${slot}`}
            >
              Remove
            </button>
          </div>
        </div>
      ) : null}

      {isAdmin ? (
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={handleFile}
          data-testid={`audio-input-${slot}`}
        />
      ) : null}
      {error ? <p className="text-rose-500 text-xs mt-3">{error}</p> : null}
    </div>
  );
};

// ============== Compact Image Uploader Area (no caption) ==============
const ImageUploaderArea = ({
  slot,
  image,
  onRefresh,
  isAdmin,
  aspect = 'aspect-[16/10]',
  rounded = 'rounded-2xl',
  emptyIconSize = 'w-10 h-10',
  emptyLabel = 'Coming soon',
  alt = '',
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = () => inputRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      const res = await fetch(`${API}/dear-dia/image`, { method: 'POST', body: fd });
      if (res.ok) await onRefresh();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const handleRemove = async (e) => {
    e?.stopPropagation();
    setUploading(true);
    try {
      await fetch(`${API}/dear-dia/image/${slot}`, { method: 'DELETE' });
      await onRefresh();
    } finally {
      setUploading(false);
    }
  };

  if (image) {
    return (
      <div className={`relative ${aspect} bg-stone-50 ${rounded} overflow-hidden border border-stone-200`} data-testid={`image-area-${slot}`}>
        <ZoomImage
          src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
          alt={alt}
          className="w-full h-full object-cover"
        />
        {isAdmin ? (
          <div className="absolute top-2 right-2 flex gap-1.5">
            <button
              onClick={handleSelect}
              className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-stone-200 text-stone-700 text-[11px] hover:bg-white transition-colors inline-flex items-center gap-1"
              data-testid={`image-area-replace-${slot}`}
            >
              <RefreshCw className="w-3 h-3" /> Replace
            </button>
            <button
              onClick={handleRemove}
              className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-stone-200 text-stone-700 text-[11px] hover:text-rose-500 transition-colors"
              data-testid={`image-area-remove-${slot}`}
            >
              Remove
            </button>
          </div>
        ) : null}
        {isAdmin ? (
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`image-area-input-${slot}`} />
        ) : null}
      </div>
    );
  }

  if (isAdmin) {
    return (
      <button
        onClick={handleSelect}
        disabled={uploading}
        className={`w-full ${aspect} ${rounded} border border-dashed border-stone-300 bg-stone-50 flex flex-col items-center justify-center gap-1.5 hover:bg-stone-100 hover:border-stone-500 transition-colors`}
        data-testid={`image-area-upload-${slot}`}
      >
        <div className={`${emptyIconSize} rounded-2xl border border-stone-200 bg-white flex items-center justify-center`}>
          <ImageIcon className="w-4 h-4 text-stone-400" />
        </div>
        <span className="text-[11px] text-stone-500">{uploading ? 'Uploading...' : 'Upload image'}</span>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`image-area-input-${slot}`} />
      </button>
    );
  }

  // Non-admin, no image
  return (
    <div
      className={`w-full ${aspect} ${rounded} bg-gradient-to-br from-stone-50 to-amber-50 border border-stone-200 flex flex-col items-center justify-center gap-1.5 text-stone-400`}
      data-testid={`image-area-empty-${slot}`}
    >
      <div className={`${emptyIconSize} rounded-2xl border border-stone-200 bg-white flex items-center justify-center`}>
        <ImageIcon className="w-4 h-4 text-stone-400" />
      </div>
      <span className="text-[10px] uppercase tracking-[0.2em]">{emptyLabel}</span>
    </div>
  );
};



// ============== Image uploader card ==============
const ImageCard = ({ slot, caption, image, onRefresh, aspect = 'aspect-[16/10]', isAdmin }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSelect = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      const res = await fetch(`${API}/dear-dia/image`, { method: 'POST', body: fd });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Upload failed');
      }
      await onRefresh();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await fetch(`${API}/dear-dia/image/${slot}`, { method: 'DELETE' });
      await onRefresh();
    } finally {
      setUploading(false);
    }
  };

  return (
    <figure
      className="rounded-3xl bg-white border border-stone-200 overflow-hidden hover:shadow-md transition-all"
      data-testid={`image-card-${slot}`}
    >
      {image ? (
        <div className={`relative ${aspect} bg-stone-50`}>
          <ZoomImage
            src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
            alt={caption}
            className="w-full h-full object-cover"
          />
          {isAdmin ? (
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                onClick={handleSelect}
                className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-stone-200 text-stone-700 text-xs hover:bg-white transition-colors inline-flex items-center gap-1"
                data-testid={`image-replace-${slot}`}
              >
                <RefreshCw className="w-3 h-3" /> Replace
              </button>
              <button
                onClick={handleRemove}
                className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-stone-200 text-stone-700 text-xs hover:text-rose-500 transition-colors"
                data-testid={`image-remove-${slot}`}
              >
                Remove
              </button>
            </div>
          ) : null}
        </div>
      ) : isAdmin ? (
        <button
          onClick={handleSelect}
          disabled={uploading}
          className={`w-full ${aspect} border-b border-dashed border-stone-200 bg-stone-50 flex flex-col items-center justify-center gap-2 hover:bg-stone-100 transition-colors`}
          data-testid={`image-upload-${slot}`}
        >
          <div className="w-12 h-12 rounded-2xl border border-stone-200 bg-white flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-stone-400" />
          </div>
          <p className="text-sm text-stone-500">{uploading ? 'Uploading...' : 'Upload image'}</p>
          <p className="text-[11px] text-stone-400">png, jpg, webp, max 10MB</p>
        </button>
      ) : (
        <div
          className={`w-full ${aspect} bg-gradient-to-br from-stone-50 to-amber-50 flex flex-col items-center justify-center gap-2 text-stone-400`}
        >
          <div className="w-12 h-12 rounded-2xl border border-stone-200 bg-white flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-stone-400" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em]">Coming soon</p>
        </div>
      )}
      <figcaption className="px-5 py-4 text-stone-600 text-[14px] leading-relaxed">
        {caption}
      </figcaption>
      {isAdmin ? (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={`image-input-${slot}`}
        />
      ) : null}
      {error ? <p className="text-rose-500 text-xs px-5 pb-4">{error}</p> : null}
    </figure>
  );
};

// ============== Brain content (used by browser tabs in FN08) ==============
const BRAIN_ITEMS = [
  {
    key: 'product-marketing',
    label: 'Product marketing',
    icon: Sparkles,
    accent: 'bg-violet-50 text-violet-500 border-violet-100',
    body: 'Turn product value into language people recognize, remember, and repeat.',
  },
  {
    key: 'ux-strategy',
    label: 'UX strategy',
    icon: Layers,
    accent: 'bg-sky-50 text-sky-500 border-sky-100',
    body: 'Look at the full journey before, during, and after adoption.',
  },
  {
    key: 'b2b-b2c',
    label: 'B2B + B2C translation',
    icon: Workflow,
    accent: 'bg-emerald-50 text-emerald-500 border-emerald-100',
    body: 'Understand both the buyer and the end user.',
  },
  {
    key: 'launch-systems',
    label: 'Launch systems',
    icon: Compass,
    accent: 'bg-amber-50 text-amber-600 border-amber-100',
    body: 'Connect positioning, enablement, audience, lifecycle, feedback, and proof.',
  },
  {
    key: 'sales-enablement',
    label: 'Sales enablement',
    icon: Megaphone,
    accent: 'bg-rose-50 text-rose-500 border-rose-100',
    body: 'Give teams the narrative, tools, and confidence to sell clearly.',
  },
  {
    key: 'customer-insights',
    label: 'Customer insights',
    icon: Search,
    accent: 'bg-indigo-50 text-indigo-500 border-indigo-100',
    body: 'Turn feedback, objections, and usage patterns into sharper messaging.',
  },
];

// ============== Folder Squares (reusable) ==============
const FolderSquares = ({ items, testId = 'folder-squares', testKeyPrefix = 'folder-tab' }) => {
  const [active, setActive] = useState(items[0].key);
  const activeItem = items.find((t) => t.key === active) || items[0];
  const ActiveIcon = activeItem.icon;
  return (
    <div className="mt-8" data-testid={testId}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {items.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              data-testid={`${testKeyPrefix}-${t.key}`}
              className={`relative pt-4 text-left transition-all duration-300 group ${
                isActive ? '-translate-y-1' : 'hover:-translate-y-0.5'
              }`}
            >
              <div
                className={`absolute top-0 left-3 h-4 w-20 rounded-t-lg border border-b-0 transition-colors ${
                  isActive
                    ? 'bg-stone-900 border-stone-900'
                    : 'bg-stone-100 border-stone-200 group-hover:bg-stone-200'
                }`}
                aria-hidden="true"
              />
              <div
                className={`relative h-28 md:h-32 rounded-xl rounded-tl-none border px-4 py-3 md:px-5 md:py-4 flex items-center gap-3 transition-all duration-300 ${
                  isActive
                    ? 'bg-white border-stone-900 shadow-lg'
                    : 'bg-[#fbfaf7] border-stone-200 group-hover:border-stone-400 group-hover:shadow-md'
                }`}
              >
                {Icon ? (
                  <div
                    className={`w-10 h-10 md:w-11 md:h-11 rounded-2xl flex items-center justify-center border shrink-0 ${t.accent}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                ) : null}
                <p className="text-stone-900 font-medium text-[13px] md:text-[15px] leading-snug">
                  {t.label}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      <div
        className="mt-6 md:mt-8 rounded-3xl bg-white border border-stone-200 p-6 md:p-9"
        data-testid={`${testKeyPrefix}-content-${activeItem.key}`}
      >
        <div className="flex items-start gap-4 md:gap-5">
          {ActiveIcon ? (
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shrink-0 ${activeItem.accent}`}>
              <ActiveIcon className="w-5 h-5" />
            </div>
          ) : null}
          <div>
            <h4 className="text-stone-900 font-medium text-xl md:text-2xl font-serif mb-3">
              {activeItem.label}
            </h4>
            <p
              className="text-stone-600 text-[16px] md:text-[17px] leading-relaxed max-w-2xl"
              style={{ textWrap: 'pretty' }}
            >
              {activeItem.body}
            </p>
            {activeItem.persona ? (
              <p
                className="text-stone-500 italic text-[15px] md:text-[16px] leading-relaxed max-w-2xl mt-3"
                style={{ textWrap: 'pretty' }}
              >
                Possible angle: {activeItem.persona}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== Browser Tabs (reusable) ==============
const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const BrowserTabs = ({ items, urlPath = 'dia.app/personas', testId = 'browser-tabs', testKeyPrefix = 'browser-tab', indicatorLabel = 'Persona' }) => {
  const [active, setActive] = useState(0);
  const item = items[active] || items[0];
  const slug = slugify(item.label);
  return (
    <div
      className="mt-8 rounded-2xl bg-white border border-stone-200 overflow-hidden shadow-sm"
      data-testid={testId}
    >
      <div className="bg-stone-100 border-b border-stone-200 px-4 py-2.5 flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-rose-300" />
        <span className="w-3 h-3 rounded-full bg-amber-300" />
        <span className="w-3 h-3 rounded-full bg-emerald-300" />
      </div>
      <div className="bg-stone-100 flex overflow-x-auto no-scrollbar">
        {items.map((it, i) => {
          const isActive = i === active;
          const Icon = it.icon;
          return (
            <button
              key={it.key || it.label}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`${testKeyPrefix}-${slugify(it.label)}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-[12px] md:text-[13px] whitespace-nowrap border-r border-stone-200 transition-colors ${
                isActive
                  ? 'bg-white text-stone-900 border-t-2 border-t-rose-400 -mt-px'
                  : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
              }`}
            >
              {Icon ? <Icon className="w-3.5 h-3.5 opacity-70" /> : <UserRound className="w-3.5 h-3.5 opacity-70" />}
              <span>{it.label}</span>
            </button>
          );
        })}
      </div>
      <div className="bg-white border-b border-stone-200 px-4 py-2 flex items-center gap-2">
        <Lock className="w-3 h-3 text-stone-400" />
        <div className="flex-1 bg-stone-50 rounded-full px-3 py-1 text-[11px] text-stone-500 font-mono truncate">
          {urlPath}/{slug}
        </div>
      </div>
      <div className="p-6 md:p-8" data-testid={`${testKeyPrefix}-content-${slugify(item.label)}`}>
        <p className="text-[11px] tracking-[0.22em] uppercase text-rose-500 font-medium mb-3">
          {indicatorLabel} &middot; {String(active + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
        </p>
        <h4 className="text-stone-900 font-serif text-2xl md:text-3xl mb-4 leading-tight">
          {item.label}
        </h4>
        <p
          className="text-stone-600 text-[16px] md:text-[17px] leading-relaxed mb-4 max-w-2xl"
          style={{ textWrap: 'pretty' }}
        >
          {item.body}
        </p>
        {item.persona ? (
          <p
            className="text-stone-500 italic text-[15px] md:text-[16px] leading-relaxed max-w-2xl"
            style={{ textWrap: 'pretty' }}
          >
            Possible angle: {item.persona}
          </p>
        ) : null}
      </div>
    </div>
  );
};

// ============== Polaroid Gallery (Field Note 10) ==============
const POLAROID_SLOTS = [
  'polaroid-1', 'polaroid-2', 'polaroid-3',
  'polaroid-4', 'polaroid-5',
];
const POLAROID_ROTATIONS = [-3, 2, -2, 3, -1];
const POLAROID_PULSES = [
  'bg-pink-300/40', 'bg-blue-300/40', 'bg-teal-300/40',
  'bg-amber-300/40', 'bg-purple-300/40',
];
const POLAROID_PRESET_IMAGES = [
  'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/0il5po20_GSV%20Cup%202022%20Naomi%20Thomas.jpg',
  'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/cf7yold1_IMG_3532.jpg',
  'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/q2k7e9ka_IMG_0506.PNG',
  'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/ltz4o6zd_IMG_9758%20%281%29.jpg',
  'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/hjzeb4wy_IMG_8994.JPG',
];

const PolaroidCard = ({ slot, index, image, onRefresh, isAdmin, presetSrc }) => {
  const inputRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [uploading, setUploading] = useState(false);
  const rotation = POLAROID_ROTATIONS[index % POLAROID_ROTATIONS.length];
  const pulse = POLAROID_PULSES[index % POLAROID_PULSES.length];

  const handleSelect = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('file', file);
      const res = await fetch(`${API}/dear-dia/image`, { method: 'POST', body: fd });
      if (res.ok) await onRefresh();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    setUploading(true);
    try {
      await fetch(`${API}/dear-dia/image/${slot}`, { method: 'DELETE' });
      await onRefresh();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="relative transition-all duration-300 cursor-pointer focus-within:z-50"
      style={{ transform: `rotate(${rotation}deg)`, zIndex: hovered ? 50 : 10 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={!presetSrc && isAdmin ? handleSelect : undefined}
      data-testid={`polaroid-${slot}`}
    >
      <div
        className={`absolute -inset-2 ${pulse} rounded-xl blur-lg animate-pulse`}
        style={{ animationDelay: `${index * 0.3}s`, animationDuration: `${2 + (index % 2)}s` }}
        aria-hidden="true"
      />
      <div
        className={`relative bg-white p-1 sm:p-1.5 rounded-md sm:rounded-lg shadow-md sm:shadow-lg transition-all duration-300 w-[60px] h-[78px] sm:w-28 sm:h-36 md:w-40 md:h-52 ${
          hovered ? 'scale-110 shadow-xl !rotate-0' : ''
        }`}
      >
        {presetSrc ? (
          <ZoomImage
            src={presetSrc}
            alt={`Moment ${index + 1}`}
            className="w-full h-full object-cover rounded"
            onClickCapture={(e) => e.stopPropagation()}
          />
        ) : image ? (
          <ZoomImage
            src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
            alt={`Moment ${index + 1}`}
            className="w-full h-full object-cover rounded"
            onClickCapture={(e) => e.stopPropagation()}
          />
        ) : isAdmin ? (
          <div className="w-full h-full rounded bg-gradient-to-br from-stone-100 to-stone-200 flex flex-col items-center justify-center gap-1 text-stone-400">
            <ImageIcon className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wider">
              {uploading ? '...' : 'Upload'}
            </span>
          </div>
        ) : (
          <div className="w-full h-full rounded bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center text-stone-300">
            <Camera className="w-5 h-5" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-2 sm:h-4 bg-white rounded-b-md sm:rounded-b-lg" aria-hidden="true" />
        {hovered && image && isAdmin ? (
          <button
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-stone-300 text-stone-500 text-xs hover:text-rose-500 shadow flex items-center justify-center"
            data-testid={`polaroid-remove-${slot}`}
          >
            ×
          </button>
        ) : null}
      </div>
      {hovered ? (
        <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-amber-400 animate-pulse" aria-hidden="true" />
      ) : null}
      {isAdmin ? (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={`polaroid-input-${slot}`}
        />
      ) : null}
    </div>
  );
};

const PolaroidGallery = ({ images, onRefresh, isAdmin }) => {
  // Always show all 5 polaroid slots — empty slots render a subtle placeholder
  const visibleSlots = POLAROID_SLOTS;
  return (
    <div className="relative py-6" data-testid="polaroid-gallery">
      <div className="hidden md:block">
        <div className="flex flex-wrap justify-center gap-y-8 gap-x-6 max-w-4xl mx-auto">
          {visibleSlots.map((slot, i) => (
            <PolaroidCard
              key={slot}
              slot={slot}
              index={i}
              image={images[slot]}
              presetSrc={POLAROID_PRESET_IMAGES[i]}
              onRefresh={onRefresh}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>
      <div className="md:hidden flex justify-center items-end gap-2 px-2">
        {visibleSlots.map((slot, i) => (
          <PolaroidCard
            key={slot}
            slot={slot}
            index={i}
            image={images[slot]}
            presetSrc={POLAROID_PRESET_IMAGES[i]}
            onRefresh={onRefresh}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
};

// ============== Page ==============
const DearDiaInner = () => {
  // (originally DearDia, kept name for minimal refactor inside DearDutchie)
  const isAdmin = useIsAdmin();
  const [clips, setClips] = useState({});
  const [images, setImages] = useState({});

  const HEADSHOT =
    'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/zcs0kzik_d56bfb62-7d28-4948-b18e-75b708f12589.png';

  const refreshClips = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dear-dia/audio`);
      if (res.ok) {
        const data = await res.json();
        setClips(data.clips || {});
      }
    } catch (e) {
      // silent
    }
  }, []);

  const refreshImages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dear-dia/image`);
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || {});
      }
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    refreshClips();
    refreshImages();
  }, [refreshClips, refreshImages]);

  useEffect(() => {
    document.title = 'Dear Dutchie, A Letter from Naomi Thomas';
  }, []);

  const audioSlots = [
    {
      slot: 'community-building',
      title: 'Community Building',
      accent: 'violet',
      quote: 'I learned how important it is to talk to your customers… & introduce a product/idea to where they gather.',
      presetUrl: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/atmztmy2_build%20communityLMU.m4a',
    },
    {
      slot: 'using-ai',
      title: 'Using AI',
      accent: 'violet',
      quote: 'We\u2019re definitely going to use that for our agency and prompting our AI tech stack.',
      presetUrl: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/g9n3ck1n_emergentfeedbackLMU.m4a',
    },
    {
      slot: 'endless-curiosity',
      title: 'Endless Curiosity',
      accent: 'violet',
      quote: 'You spoke with such passion, energy and love for what you do, and as a Founder, I really appreciated that.',
      presetUrl: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/2kiuupbr_energyfeedbackLMU.m4a',
    },
    {
      slot: 'problem-solving',
      title: 'Problem Solving',
      accent: 'violet',
      quote: 'Not being too attached to one specific solution… it will evolve over time.',
      presetUrl: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/v5q8xtty_marriedtoproblemnotsolution.m4a',
    },
  ];

  const tools = [
    { name: 'Dutchie', role: 'Consumer and business product research', icon: Compass, accent: 'emerald',
      presetLogo: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/3rca1aec_dutchielogo.png' },
    { name: 'Notion', role: 'Notes, role alignment, and synthesis', icon: NotebookPen, accent: 'stone',
      presetLogo: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/qc4ayh77_Notion-Logo-Vector-300x300.png' },
    { name: 'ChatGPT', role: 'Copy development and structure', icon: MessageSquare, accent: 'teal',
      presetLogo: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/084lb4og_ChatGPT-Logo.png' },
    { name: 'Emergent', role: 'Built this page', icon: Sparkles, accent: 'amber',
      presetLogo: 'https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/maqqqm8h_emergent.jpg' },
  ];

  const toolAccent = {
    violet: 'bg-violet-50 border-violet-100 text-violet-500',
    stone: 'bg-stone-100 border-stone-200 text-stone-600',
    amber: 'bg-amber-50 border-amber-100 text-amber-600',
    teal: 'bg-teal-50 border-teal-100 text-teal-500',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-500',
    sky: 'bg-sky-50 border-sky-100 text-sky-500',
    rose: 'bg-rose-50 border-rose-100 text-rose-500',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-500',
  };

  const useCases = [
    {
      key: 'monday-reset',
      label: 'The Monday Morning Reset',
      body: 'For users who start the week with a pile of tabs, meetings, messages, and half finished tasks.',
      persona: 'Start the week with context already gathered.',
      icon: Compass,
      accent: 'bg-sky-50 text-sky-500 border-sky-100',
    },
    {
      key: 'meeting-prep',
      label: 'The Meeting Prep Flow',
      body: 'For users who join calls while scrambling for the right doc, note, email, or brief.',
      persona: 'Walk into the meeting with the room already read.',
      icon: MessageCircle,
      accent: 'bg-amber-50 text-amber-600 border-amber-100',
    },
    {
      key: 'research-sprint',
      label: 'The Research Sprint',
      body: 'For users gathering links, docs, insights, and screenshots across multiple sources.',
      persona: 'Turn scattered research into something usable.',
      icon: Search,
      accent: 'bg-teal-50 text-teal-500 border-teal-100',
    },
    {
      key: 'founder-brief',
      label: 'The Founder Brief',
      body: 'For founders moving across investors, hiring, product, team updates, and customer feedback.',
      persona: 'Keep the company context close without carrying all of it in your head.',
      icon: TrendingUp,
      accent: 'bg-violet-50 text-violet-500 border-violet-100',
    },
    {
      key: 'student-builder',
      label: 'The Student Builder Workflow',
      body: 'For students balancing class, entrepreneurship, research, applications, and projects.',
      persona: 'A browser that helps ambitious students stay clear while building.',
      icon: GraduationCap,
      accent: 'bg-emerald-50 text-emerald-500 border-emerald-100',
    },
    {
      key: 'pmm-workflow',
      label: 'The Product Marketer Workflow',
      body: 'For PMMs working across customer insights, launch briefs, docs, positioning, meetings, and competitive research.',
      persona: 'From scattered context to sharper market language.',
      icon: Megaphone,
      accent: 'bg-rose-50 text-rose-500 border-rose-100',
    },
  ];

  const growthAreas = [
    {
      title: 'Founder and builder communities',
      copy: 'Founder houses, accelerator communities, startup programs, VC portfolio networks, founder newsletters, and indie builder spaces.',
      why:
        'Founders move across research, team updates, investor prep, customer calls, product decisions, and hiring. They feel the pain daily.',
    },
    {
      title: 'Student innovation communities',
      copy: 'University innovation labs, entrepreneurship clubs, hackathons, student founder programs, and campus builder groups.',
      why: 'Students adopt new tools quickly when the tool helps them feel sharper, faster, and more prepared.',
    },
    {
      title: 'Product and design communities',
      copy: 'PM groups, design communities, product ops networks, Figma adjacent communities, and startup operator circles.',
      why: 'These users live across docs, research, customer feedback, meetings, and decisions.',
    },
    {
      title: 'AI workflow communities',
      copy: 'AI tool communities, automation circles, productivity creators, workflow YouTubers, and newsletter ecosystems.',
      why: 'These users already test new work behaviors and can make the value of Dia visible.',
    },
    {
      title: 'Consultant and operator networks',
      copy: 'Fractional leaders, chief of staff groups, consultants, strategists, agency operators, and client service communities.',
      why:
        'They constantly switch context and need faster ways to prepare, synthesize, and deliver.',
    },
    {
      title: 'Tool power users',
      copy: 'People who live in Notion, Slack, Gmail, Calendar, Google Docs, Figma, and similar tools.',
      why: 'Dia can meet them inside the workflows they already depend on.',
    },
  ];

  return (
    <main
      className="min-h-screen bg-[#fbfaf7] text-stone-900 antialiased"
      data-testid="dear-dia-page"
    >
      <AnalyticsTracker />
      <CursorTrail />

      {/* ===================== HERO ===================== */}
      <Section id="hero" className="pt-24 md:pt-32 pb-6">
        <div className="mb-8">
          <Eyebrow color="bg-rose-50 text-rose-500 border-rose-100">
            <Sparkles className="w-3 h-3" />
            Director of Product Marketing Application
          </Eyebrow>
        </div>
        <h1
          className="text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-serif leading-[1.02] text-stone-900 tracking-tight mb-4"
          data-testid="dear-dia-hero-title"
          style={{ textWrap: 'balance' }}
        >
          I found Dutchie on a Thursday.
        </h1>
        <p className="text-stone-500 text-[18px] md:text-[20px] italic mb-10">
          June 11th 2026 to be exact.
        </p>
        <p
          className="text-stone-600 text-[18px] md:text-[22px] leading-[1.6] max-w-3xl mb-6"
          style={{ textWrap: 'pretty' }}
        >
          I was looking to order products online, clicked into Dutchie, and went down a rabbit hole
          from the consumer experience to the business platform to the careers page.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] max-w-3xl mb-6"
          style={{ textWrap: 'pretty' }}
        >
          That path immediately interested me because Dutchie sits at a rare product marketing
          intersection: B2C discovery, B2B infrastructure, regulated commerce, retailer growth, and
          customer trust.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] max-w-3xl"
          style={{ textWrap: 'pretty' }}
        >
          Dear Dutchie is my application told through field notes: what I noticed, what I
          researched, and how I think about helping people understand, adopt, and trust product
          behaviors.
        </p>
      </Section>

      {/* ===================== TOOLS ===================== */}
      <Section id="tools" className="pt-4 pb-16">
        <p className="text-[11px] tracking-[0.22em] uppercase text-stone-500 mb-3">
          Tools Used
        </p>
        <h2
          className="text-2xl md:text-3xl font-serif text-stone-900 mb-12 max-w-2xl"
          style={{ textWrap: 'balance' }}
        >
          What I used to build this.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {tools.map((t) => {
            const slug = t.name.toLowerCase().replace(/\s+/g, '-');
            return (
              <div
                key={t.name}
                className="rounded-3xl bg-white border border-stone-200 p-5 md:p-6 hover:shadow-md hover:border-stone-300 transition-all flex flex-col items-center text-center gap-3"
                data-testid={`tool-card-${slug}`}
              >
                <div className="w-16 h-16 md:w-20 md:h-20">
                  {t.presetLogo ? (
                    <div className="w-full h-full rounded-2xl overflow-hidden bg-white border border-stone-200 flex items-center justify-center">
                      <ZoomImage
                        src={t.presetLogo}
                        alt={`${t.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <ImageUploaderArea
                      slot={`tool-${slug}`}
                      image={images[`tool-${slug}`]}
                      onRefresh={refreshImages}
                      isAdmin={isAdmin}
                      aspect="aspect-square"
                      rounded="rounded-2xl"
                      emptyIconSize="w-7 h-7"
                      alt={`${t.name} logo`}
                      emptyLabel="Logo"
                    />
                  )}
                </div>
                <div>
                  <p className="text-stone-900 font-medium text-[15px] md:text-base">{t.name}</p>
                  <p
                    className="text-stone-500 text-[12px] md:text-[13px] leading-relaxed mt-0.5"
                    style={{ textWrap: 'pretty' }}
                  >
                    {t.role}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ===================== DEAR DIA DIVIDER ===================== */}
      <section
        id="dear-dia-divider"
        className="relative bg-stone-950 text-stone-100 px-6 md:px-10 lg:px-16 py-28 md:py-40 border-y border-stone-900"
        data-testid="dear-dia-divider"
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] tracking-[0.28em] uppercase text-rose-300/80 font-medium mb-8">
            Field Notes
          </p>
          <h2
            className="text-6xl sm:text-7xl md:text-8xl lg:text-[120px] font-serif leading-[0.95] tracking-tight text-white mb-10"
            style={{ textWrap: 'balance' }}
            data-testid="dear-dia-divider-title"
          >
            Dear Dutchie
          </h2>
          <p
            className="text-stone-300 text-[17px] md:text-[20px] leading-[1.7] max-w-2xl"
            style={{ textWrap: 'balance' }}
          >
            What follows are my notes: what I noticed, what I researched, and how I think about
            helping customers understand, adopt, and grow with Dutchie.
          </p>
        </div>
      </section>


      {/* ============================================================ */}
      {/* ============== FIELD NOTES 01 - 07 ========================= */}
      {/* ============================================================ */}

      {/* FIELD NOTE 01 */}
      <Section id="field-note-01">
        <FieldNoteHeader number="01" title="Dear Dutchie, you caught me through the consumer door." />
        <Para>
          I was looking to order products online, and the experience pulled me into the broader
          ecosystem: local dispensaries, product discovery, pickup and delivery, payments, and then
          the business platform behind it all.
        </Para>
        <Para>
          That path stood out because Dutchie is not only selling software. Dutchie is shaping how
          cannabis commerce feels for both sides of the market.
        </Para>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 mt-10">
          <SmallCard
            icon={Heart}
            accent="rose"
            title="For consumers"
            copy="Can I find what I need, trust the experience, and order easily?"
          />
          <SmallCard
            icon={TrendingUp}
            accent="emerald"
            title="For dispensaries"
            copy="Can I run, grow, and modernize my business with less friction?"
          />
        </div>
      </Section>

      {/* FIELD NOTE 02 */}
      <Section id="field-note-02" className="bg-white border-y border-stone-100">
        <FieldNoteHeader number="02" title="Dear Dutchie, your product story has two audiences." />
        <Para>Dutchie has to speak to consumers and businesses at the same time.</Para>
        <Para>
          On the consumer side, the story is about access, ease, local discovery, trust, and
          convenience.
        </Para>
        <Para>
          On the business side, the story is about growth, operations, compliance, customer data,
          payments, loyalty, and modern commerce infrastructure.
        </Para>
        <Para>That duality is what makes this role interesting to me.</Para>
        <div className="grid md:grid-cols-3 gap-4 md:gap-5 mt-10">
          <SmallCard
            icon={Compass}
            accent="sky"
            title="B2C lens"
            copy="Help consumers understand how to discover, order, pickup, and return."
          />
          <SmallCard
            icon={Workflow}
            accent="violet"
            title="B2B lens"
            copy="Help dispensaries understand how Dutchie helps them operate, grow, and serve customers."
          />
          <SmallCard
            icon={Sparkles}
            accent="amber"
            title="Shared lens"
            copy="Make cannabis commerce feel easier, safer, smarter, and more trusted."
          />
        </div>
      </Section>

      {/* FIELD NOTE 03 */}
      <Section id="field-note-03">
        <FieldNoteHeader number="03" title="Dear Dutchie, the real challenge is clarity." />
        <Para>
          The business platform is powerful, but product marketing has to help different audiences
          understand what matters first.
        </Para>
        <Para>
          A dispensary owner, operator, marketer, budtender, finance lead, and ecommerce manager may
          all care about different parts of the platform.
        </Para>
        <Para>
          That means the product story needs to connect the big promise to practical use cases.
        </Para>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 mt-10">
          <SmallCard
            icon={Layers}
            accent="emerald"
            title="What does Dutchie help someone do today?"
            copy="Run the store, manage inventory, process payments, personalize marketing, improve ecommerce, understand customers, and grow revenue."
          />
          <SmallCard
            icon={Heart}
            accent="rose"
            title="What does Dutchie help someone feel?"
            copy="More in control, more informed, more modern, and more prepared to compete."
          />
        </div>
      </Section>

      {/* FIELD NOTE 04 */}
      <Section id="field-note-04" className="bg-white border-y border-stone-100">
        <FieldNoteHeader number="04" title="Dear Dutchie, I would think in customer journeys." />
        <Para>
          For this role, I would start by mapping the journey across both sides of the ecosystem.
        </Para>

        {(() => {
          const journeys = [
            {
              key: 'consumer',
              eyebrow: 'Consumer journey',
              accent: 'rose',
              steps: ['Search', 'Discover', 'Compare', 'Order', 'Pay', 'Pickup or delivery', 'Return'],
            },
            {
              key: 'retailer',
              eyebrow: 'Retailer journey',
              accent: 'emerald',
              steps: ['Evaluate', 'Onboard', 'Launch', 'Train team', 'Operate', 'Market', 'Analyze', 'Expand'],
            },
            {
              key: 'pmm',
              eyebrow: 'Product marketing work',
              accent: 'violet',
              steps: [
                'Positioning',
                'Sales enablement',
                'Lifecycle content',
                'Competitive intel',
                'Customer stories',
                'Feedback loops',
              ],
            },
          ];
          const accents = {
            rose: 'bg-rose-50 text-rose-600 border-rose-100',
            emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
            violet: 'bg-violet-50 text-violet-600 border-violet-100',
          };
          const dots = {
            rose: 'bg-rose-300',
            emerald: 'bg-emerald-300',
            violet: 'bg-violet-300',
          };
          return (
            <div className="mt-10 space-y-5" data-testid="journey-roadmap">
              {journeys.map((j) => (
                <div
                  key={j.key}
                  className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6 shadow-sm"
                  data-testid={`journey-${j.key}`}
                >
                  <p className="text-[11px] tracking-[0.22em] uppercase text-stone-500 mb-4">
                    {j.eyebrow}
                  </p>
                  <div className="-mx-1 px-1 overflow-x-auto no-scrollbar">
                    <div className="flex items-center gap-1.5 md:gap-2 flex-nowrap min-w-max">
                      {j.steps.map((step, i) => (
                        <React.Fragment key={step}>
                          <div
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border ${accents[j.accent]} font-medium text-[11px] md:text-[12px] whitespace-nowrap`}
                          >
                            <span
                              className={`inline-block w-1 h-1 rounded-full ${dots[j.accent]}`}
                              aria-hidden="true"
                            />
                            {step}
                          </div>
                          {i < j.steps.length - 1 ? (
                            <ArrowRight className="w-3 h-3 text-stone-300 shrink-0" aria-hidden="true" />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        <CalloutCard>
          The goal is making each audience understand the product in the language of their own day.
        </CalloutCard>
      </Section>

      {/* FIELD NOTE 05 */}
      <Section id="field-note-05">
        <FieldNoteHeader number="05" title="Dear Dutchie, launch readiness is a system." />
        <Para>A strong launch is not only the announcement.</Para>
        <Para>
          It is the message, the audience, the enablement, the customer proof, the internal
          alignment, the lifecycle plan, and the feedback system after launch.
        </Para>
        <Para>For Dutchie, I would think about launches through a few questions:</Para>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 mt-8">
          <SmallCard
            icon={UserRound}
            accent="emerald"
            title="Who needs this most?"
            copy="Retailers, operators, marketers, payment teams, ecommerce teams, or consumers."
          />
          <SmallCard
            icon={Repeat}
            accent="violet"
            title="What behavior should change?"
            copy="Adopt a workflow, try a feature, migrate a process, train a team, or understand a new product line."
          />
          <SmallCard
            icon={Megaphone}
            accent="rose"
            title="What does the sales team need?"
            copy="Narrative, talk tracks, objection handling, competitive framing, proof points, and customer examples."
          />
          <SmallCard
            icon={Search}
            accent="amber"
            title="What should we learn after launch?"
            copy="Which message resonates, which audience converts, which feature creates pull, and which customer stories should shape the next iteration."
          />
        </div>
      </Section>

      {/* FIELD NOTE 06 */}
      <Section id="field-note-06" className="bg-white border-y border-stone-100">
        <FieldNoteHeader number="06" title="Dear Dutchie, insights should travel." />
        <Para>
          What stood out to me about the role is the need to connect product, sales, customer
          success, marketing, and leadership.
        </Para>
        <Para>That is where product marketing can become the connective tissue.</Para>
        <ul className="text-stone-600 text-[17px] md:text-[19px] leading-[1.85] list-none space-y-1 mb-6 max-w-3xl mt-2">
          <li>Customer feedback should inform messaging.</li>
          <li>Sales objections should inform enablement.</li>
          <li>Product usage should inform lifecycle.</li>
          <li>Competitive patterns should inform positioning.</li>
          <li>Retailer stories should inform campaigns.</li>
        </ul>
        <CalloutCard>
          The best product marketing systems do not let insights sit still. They move.
        </CalloutCard>
      </Section>

      {/* FIELD NOTE 07 - about me (was FN06) */}
      <Section id="field-note-07">
        <FieldNoteHeader number="07" title="Dear Dutchie, a little about me." />

        <div
          className="rounded-3xl bg-gradient-to-br from-stone-900 via-stone-900 to-stone-800 p-6 md:p-10 mb-12 shadow-xl"
          data-testid="about-me-widget"
        >
          <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-8 items-center">
            <div className="rounded-2xl overflow-hidden border border-stone-700/60 aspect-square w-44 md:w-full max-w-[220px] mx-auto md:mx-0">
              <ZoomImage src={HEADSHOT} alt="Naomi Thomas headshot" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-emerald-300 text-[11px] tracking-[0.22em] uppercase font-medium mb-2">
                Director of Product Marketing Application
              </p>
              <h3 className="text-white text-3xl md:text-4xl font-serif mb-4">Naomi Thomas</h3>
              <p
                className="text-stone-300 text-[15px] md:text-[16px] leading-relaxed mb-5"
                style={{ textWrap: 'pretty' }}
              >
                Product marketer, founder, creative strategist, and data dreamer. Human centered
                UX. Somewhere data dreaming.
              </p>
              <div className="flex flex-wrap gap-4 text-stone-300 text-sm">
                <a
                  href="mailto:me@iamnaomithomas.com"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                  data-testid="about-me-email"
                >
                  <Mail className="w-4 h-4" /> me@iamnaomithomas.com
                </a>
                <a
                  href="https://www.linkedin.com/in/naomi-rae-thomas/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                  data-testid="about-me-linkedin"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>

        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
          style={{ textWrap: 'balance' }}
        >
          I am Naomi Thomas, a product marketer, founder, creative strategist, and data dreamer.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
          style={{ textWrap: 'balance' }}
        >
          I built my first computer at six years old, later deepened my technical foundation
          through computer science programs, ran a digital marketing agency with a 30 person global
          creative team, and co founded an edtech startup that was acquired.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
          style={{ textWrap: 'balance' }}
        >
          My work sits across product marketing, UX strategy, AI native workflows, community led
          growth, and human centered design.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
          style={{ textWrap: 'balance' }}
        >
          I have contributed to approximately $20M in valuation growth during acquisition and
          helped secure over $1M in grant and research funding across edtech product work.
        </p>
        <p
          className="text-stone-600 text-[17px] md:text-[19px] leading-[1.7] mb-5 max-w-3xl"
          style={{ textWrap: 'balance' }}
        >
          I am drawn to products that reduce friction, create clarity, and help people move through
          work with more confidence.
        </p>

        {/* Polaroid gallery (uploadable moments) */}
        <div className="mt-16">
          <PolaroidGallery images={images} onRefresh={refreshImages} isAdmin={isAdmin} />
        </div>
      </Section>

      {/* ===================== COMMUNITY + AUDIO (after FN05 moments) ===================== */}
      <Section id="community" className="py-20">
        <div className="mb-6">
          <Eyebrow color="bg-violet-50 text-violet-600 border-violet-100">
            <GraduationCap className="w-3 h-3" /> From websites to rooms
          </Eyebrow>
        </div>
        <h2
          className="text-3xl md:text-5xl font-serif leading-tight mb-6 max-w-4xl"
          style={{ textWrap: 'balance' }}
        >
          I like building things people can learn from.
        </h2>
        <Para>
          That same instinct shows up in how I speak, teach, and share resources. At the end of
          February, I spoke at a private institution with students and builders about creative
          strategy, AI tools, entrepreneurship, and human centered building.
        </Para>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-10">
          {audioSlots.map((s) => (
            <AudioCard
              key={s.slot}
              slot={s.slot}
              title={s.title}
              accent={s.accent}
              quote={s.quote}
              presetUrl={s.presetUrl}
              clip={clips[s.slot]}
              onRefresh={refreshClips}
              isAdmin={isAdmin}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-stone-900 font-medium text-[18px] md:text-[20px]">February 25, 2026</p>
          <p className="text-stone-400 text-[14px] md:text-[15px] mt-2">
            Private University in Los Angeles, CA
          </p>
        </div>

        <div className="mt-8 mx-auto max-w-md">
          <ZoomImage
            src="https://customer-assets.emergentagent.com/job_5c652d70-82f1-48c0-872a-39b04ce7ce0c/artifacts/hr6yoi56_lmu-students.jpg"
            alt="Naomi Thomas with students at a private university in Los Angeles"
            className="w-full h-auto rounded-2xl border border-stone-200 shadow-sm"
            data-testid="student-talk-image"
          />
        </div>
      </Section>

      {/* FIELD NOTE 08 - brain works rendered as browser tabs */}
      <Section id="field-note-08" className="bg-white border-y border-stone-100">
        <FieldNoteHeader number="08" title="Dear Dutchie, here's how my brain works." />
        <Para>I think in systems, feelings, and behavior.</Para>
        <BrowserTabs
          items={BRAIN_ITEMS}
          urlPath="naomi.brain"
          testId="brain-browser-tabs"
          testKeyPrefix="brain-tab"
          indicatorLabel="Tab"
        />
      </Section>

      {/* ===================== FINAL NOTE ===================== */}
      <Section id="final-note" className="pb-32">
        <div className="mb-6">
          <Eyebrow color="bg-stone-900 text-white border-stone-900">
            <Heart className="w-3 h-3" /> Final Note
          </Eyebrow>
        </div>
        <h2
          className="text-3xl md:text-5xl font-serif leading-tight mb-8 max-w-4xl"
          style={{ textWrap: 'balance' }}
        >
          Dear Dutchie, I would love to help customers understand you.
        </h2>
        <Para>You sit in a category where clarity matters.</Para>
        <Para>
          Cannabis commerce is operational, emotional, local, regulated, and deeply human.
          Consumers want access and trust. Retailers want growth and control. Teams need tools that
          make the work easier, not harder.
        </Para>
        <Para>That makes the product marketing challenge meaningful.</Para>
        <Para>
          I would bring curiosity, taste, intensity, and a deep respect for both sides of the
          customer journey. I would help make Dutchie easier to understand, easier to adopt, and
          easier to grow with.
        </Para>

        <div className="mt-12 mb-12">
          <p className="text-stone-700 text-[17px]">With curiosity,</p>
          <p
            className="text-stone-900 text-3xl md:text-4xl font-serif italic mt-2"
            data-testid="dear-dia-signoff"
          >
            Naomi
          </p>
        </div>
      </Section>
    </main>
  );
};

const DearDutchie = () => (
  <LightboxProvider>
    <DearDiaInner />
  </LightboxProvider>
);

export default DearDutchie;
